import { useState } from 'react';
import { snakeCase } from 'change-case';
import { isUndefined } from 'lodash-es';

import { getDocumentIncludesForRelationshipObject } from '~/core/application/workflow/utils';
import { useTenantedApplicationConfig } from '../../../../contexts';
import { useWorkflowSession } from '../../../../contexts/WorkflowSessionContext/hooks/useWorkflowSession';
import { useRootDocumentOrFail } from '../../../../hooks';
import { useEventHandler } from '../../../../hooks/useEventHandler';
import {
  Document,
  DocumentRelationshipObject,
  Entity,
  WorkflowState,
} from '../../../../types';
import { getDocumentTargetAndLocationForPath } from '../../../../utils';
import { UpdateOrCreateDocumentFn } from '../../types/useUpdateOrCreateDocument';
import { useApplyDocumentPatch } from '../useApplyDocumentPatch';
import { useAttachDocumentRelation } from '../useAttachDocumentRelation';
import { useCreateDocument } from '../useCreateDocument';

export const useUpdateOrCreateDocument = () => {
  const rootDocument = useRootDocumentOrFail();
  const { rehydrateRootDocument, workflowName } = useWorkflowSession();
  const config = useTenantedApplicationConfig();
  const { applyDocumentPatch } = useApplyDocumentPatch();
  const { createDocument } = useCreateDocument();
  const { attachDocumentRelation } = useAttachDocumentRelation();
  const [isLoading, setIsLoading] = useState(false);

  const updateOrCreateDocument: UpdateOrCreateDocumentFn = useEventHandler(
    async <T extends string>({
      data,
      definition,
      documentPath = [],
    }: {
      data: Entity;
      definition: T;
      documentPath?: string[];
    }) => {
      if (!workflowName) {
        throw new Error(
          'Cannot update or create document: active workflow is missing.',
        );
      }
      const activeWorkflowName = workflowName;
      const cfg = config;
      const [target] = getDocumentTargetAndLocationForPath(
        rootDocument,
        documentPath,
      );

      const [relationship, relationshipKey] =
        documentPath.length > 0
          ? documentPath
              .map((docSubPath) => docSubPath.split('.')[0])
              .reduce<
                [DocumentRelationshipObject, string]
              >(([relationshipAcc], docSubPath) => [relationshipAcc.relationships ? relationshipAcc.relationships[docSubPath] : relationshipAcc, docSubPath], [cfg.getWorkflowConfig(activeWorkflowName).rootDocument as DocumentRelationshipObject, ''])
          : [null, ''];

      const targetIsArray = Array.isArray(target);
      const dataId = data?.id as string | undefined;
      const targetId = !targetIsArray ? target?.id : undefined;
      const documentId = dataId ?? targetId;
      const isNewDocument = !documentId;

      let includes: string[] = [];
      if (!relationship) {
        includes = getDocumentIncludesForRelationshipObject(
          cfg.getWorkflowConfig(activeWorkflowName).rootDocument.relationships,
        );
      } else if (relationship?.relationships) {
        includes = getDocumentIncludesForRelationshipObject(
          relationship.relationships,
        );
      }

      setIsLoading(true);
      try {
        const response = await (documentId
          ? applyDocumentPatch({
              id: documentId,
              documentParams: { definition, workflow: activeWorkflowName },
              data,
            })
          : createDocument({
              definitionName: definition,
              workflowName: activeWorkflowName,
              data,
              includes,
            }));

        if (!response) throw new Error('Failed to update or create document');

        const { data: document, meta } = response;

        if (isNewDocument && relationship) {
          if (isUndefined(rootDocument)) {
            throw new Error(
              'Failed to update or create document: cannot create related document without root document.',
            );
          }
          let parentDocument: Document = rootDocument;
          if (documentPath.length > 1) {
            const [parentTarget] = getDocumentTargetAndLocationForPath(
              rootDocument,
              documentPath.slice(0, -1),
            );
            parentDocument = parentTarget as Document;
          }
          await attachDocumentRelation({
            documentId: parentDocument.id,
            documentParams: {
              definition: parentDocument.definition,
              workflow: activeWorkflowName,
            },
            data: {
              [snakeCase(relationshipKey)]:
                relationship.cardinality === 'hasOne'
                  ? document.id
                  : [document.id],
            },
          });
        }

        rehydrateRootDocument();

        return [
          document,
          relationship?.cardinality === 'hasMany'
            ? [
                ...documentPath.slice(0, -1),
                `${relationshipKey}.${document.id}`,
              ]
            : documentPath,
          meta as WorkflowState | undefined,
        ];
      } finally {
        setIsLoading(false);
      }
    },
  );

  return { updateOrCreateDocument, isLoading };
};
